import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type GroupBase } from "react-select";

import {
  useFetchContigFeatureSelectorStatistics,
  useFetchNucleotideFeatureSelectorStatistics,
} from "@/ui-modules/entities/genomes/graphql/useFetchGenomeStatistics.ts";

import {
  type Contig,
  type InputTableQuerySpec,
  type MarkerStats,
  type NuFeatureStats,
  type Organism,
} from "../../graphql.js";
import { updateLastOpenNucleotideFeatureTableSpec } from "../../redux/modules/genome.js";
import { createTableSpecForContigParent, createTableSpecForOrganismParent } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { ENTITY_TYPE } from "../../settings/settings.js";
import GenomicFeatureSelector, { type IFeatureOption } from "./GenomicFeatureSelector.js";

interface IGenomeNucleotideFeatureSelectorProps {
  genome: Organism;
}

const GenomeNucleotideFeatureSelector = (props: IGenomeNucleotideFeatureSelectorProps) => {
  const genomeState = useSelector((state: IAppState) => state.genome);

  const createTableQuerySpecForParent = useCallback(
    (entityType: string, featureTypeId: number) => {
      return createTableSpecForOrganismParent(entityType, props.genome.id, null, featureTypeId);
    },
    [props.genome],
  );

  const { data } = useFetchNucleotideFeatureSelectorStatistics({ genomeId: props.genome?.id });

  return (
    data?.genomeStatistics?.nuFeaturesCount &&
    data?.genomeStatistics?.markersCount && (
      <NucleotideFeatureSelector
        lastOpenFeatureTableSpec={genomeState.lastOpenFeatureTableSpec}
        createTableQuerySpecForParent={createTableQuerySpecForParent}
        nuFeatureStats={data?.genomeStatistics?.nuFeaturesCount as NuFeatureStats[]}
        markerStats={data?.genomeStatistics?.markersCount as MarkerStats[]}
      />
    )
  );
};

interface IContigNucleotideFeatureSelectorProps {
  contig: Contig;
}

const ContigNucleotideFeatureSelector = (props: IContigNucleotideFeatureSelectorProps) => {
  const genomeState = useSelector((state: IAppState) => state.genome);

  const createTableQuerySpecForParent = useCallback(
    (entityType: string, featureTypeId: number) => {
      return createTableSpecForContigParent(entityType, props.contig.id, featureTypeId);
    },
    [props.contig],
  );

  const { data } = useFetchContigFeatureSelectorStatistics({
    contigId: props.contig?.id,
  });

  return (
    data?.contigStatistics?.nuFeaturesCount &&
    data?.contigStatistics?.markersCount && (
      <NucleotideFeatureSelector
        lastOpenFeatureTableSpec={genomeState.lastOpenFeatureTableSpec}
        createTableQuerySpecForParent={createTableQuerySpecForParent}
        nuFeatureStats={data?.contigStatistics?.nuFeaturesCount as NuFeatureStats[]}
        markerStats={data?.contigStatistics?.markersCount as MarkerStats[]}
      />
    )
  );
};

interface INucleotideFeatureSelectorProps {
  lastOpenFeatureTableSpec: InputTableQuerySpec;
  createTableQuerySpecForParent: (entityType: string, featureTypeId: number) => InputTableQuerySpec;
  nuFeatureStats: NuFeatureStats[];
  markerStats: MarkerStats[];
}

const NucleotideFeatureSelector = (props: INucleotideFeatureSelectorProps) => {
  const [optionList, setOptionList] = useState<IFeatureOption[]>(null);
  const dispatch = useDispatch<ReduxDispatch>();

  useEffect(() => {
    const nuFeatureOptions: IFeatureOption[] =
      props.nuFeatureStats?.map((n) => {
        return {
          label: n.featureTypeName,
          color: n.featureTypeColor,
          showColorBadge: false, // do not show the color badge anymore
          tableQuerySpec: props.createTableQuerySpecForParent(ENTITY_TYPE.NUFEATURE, n.featureTypeId),
        };
      }) || [];

    const markerOptions: IFeatureOption[] =
      props.markerStats?.map((m) => {
        return {
          label: m.markerTypeName,
          color: null,
          showColorBadge: false,
          tableQuerySpec: props.createTableQuerySpecForParent(ENTITY_TYPE.MARKER_NUS, m.markerTypeId),
        };
      }) || [];

    const options: IFeatureOption[] = nuFeatureOptions.concat(markerOptions);
    setOptionList(options);
  }, [props.createTableQuerySpecForParent]);

  return (
    <GenomicFeatureSelector
      options={optionList}
      setLastOpenFeatureTableSpec={(tableSpec: InputTableQuerySpec) => {
        dispatch(updateLastOpenNucleotideFeatureTableSpec(tableSpec));
      }}
      lastOpenFeatureTableSpec={props.lastOpenFeatureTableSpec}
      groupOptions={groupOptions}
    />
  );
};

function groupOptions(optionList: IFeatureOption[]) {
  const options: GroupBase<IFeatureOption>[] = [];

  if (!optionList) {
    return;
  }

  const featureOptions: IFeatureOption[] = optionList.filter(
    (option) => option.tableQuerySpec.resultType === ENTITY_TYPE.NUFEATURE,
  );
  if (featureOptions.length) {
    options.push({
      label: "Nucleotide Features",
      options: featureOptions,
    });
  }
  const markerOptions: IFeatureOption[] = optionList.filter(
    (option) => option.tableQuerySpec.resultType === ENTITY_TYPE.MARKER_NUS,
  );
  if (markerOptions.length) {
    options.push({
      label: "Markers",
      options: markerOptions,
    });
  }
  return options;
}

export { ContigNucleotideFeatureSelector, GenomeNucleotideFeatureSelector };
